import { PassengerLegStatus } from '@eti/schema-types';
import { Stack, Text, textColors, textVariants } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { constants } from '@eti/utils';
import type { TravelersType, TripSegmentType } from '../../../models';
import AirlineReferenceInfo from '../../common/AirlineReferenceInfo';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import { getAirlineReferenceEmail } from './utils';
import UploadDocumentsLink from './UploadDocumentsLink';

const bulletListStyles = css`
  list-style: unset;
  padding-inline-start: 24px;
  width: 100%;

  & li:not(:last-child) {
    padding-bottom: 4px;
  }
`;

const TravelersList = ({ travelers }: { travelers: TravelersType }) => (
  <ul className={bulletListStyles}>
    {travelers.map((traveler, index) => (
      <li key={index}>
        <Text className={constants.MOUSE_FLOW_CLASS_NAMES} variant={textVariants._16_BOLD}>
          {traveler.name}
        </Text>
      </li>
    ))}
  </ul>
);

interface PartialStatusCheckInInfoProps {
  checkInStatus: PassengerLegStatus;
  travelers: TravelersType;
  segment: TripSegmentType;
}

const PartialStatusCheckInInfo = ({
  checkInStatus,
  travelers,
  segment,
}: PartialStatusCheckInInfoProps) => {
  const { t } = useTranslation();
  const { customerDetails, flightInformation } = useOrderDetails();
  const { origin, destination, departuredAt, arrivedAt, carrierBookingNumber } = segment;

  switch (checkInStatus) {
    case PassengerLegStatus.Success:
      return <Text>{t('CheckIn.Purchased.PartialSuccess.Description')}</Text>;
    case PassengerLegStatus.Suspended:
      return (
        <>
          <Stack spacing={8}>
            <Text>{t('CheckIn.Purchased.PartialMissingDocs.Description')}</Text>
            <TravelersList travelers={travelers} />
            <UploadDocumentsLink />
          </Stack>
          <Text color={textColors.LIGHTER}>
            {t('CheckIn.Purchased.MissingDocs.Disclaimer.Text')}
          </Text>
        </>
      );
    case PassengerLegStatus.Missed:
    case PassengerLegStatus.Failed:
      return (
        <AirlineReferenceInfo
          airlineReferenceInfo={{
            destination,
            origin,
            arrivedAt,
            departuredAt,
            emailInfo: getAirlineReferenceEmail(
              flightInformation,
              customerDetails.email,
              segment.pnr,
            ),
            carrierBookingNumber,
          }}
          dataTestId="check-in-status-partial-failure"
          descriptionContent={
            <Stack spacing={8}>
              <Text>{t('CheckIn.Purchased.PartialFailure.Description')}</Text>
              <TravelersList travelers={travelers} />
            </Stack>
          }
        />
      );
    default:
      return null;
  }
};

export default PartialStatusCheckInInfo;
